/**
 * useNovidadeDaHora — a vitrine do que foi feito, uma novidade por hora.
 *
 * Valdair, 21/09/2026: "a questão é sempre no topo ter um trabalho novo feito.
 * Cada mensagem fica no máximo uma hora. Se entrar mais do que uma nessa hora,
 * continua mostrando a mesma — só muda quando muda a hora. Se em pouco tempo eu
 * fiz mais deploys, não tem problema: ajuda no tempo mais longo sem deploy."
 *
 * Quem escolhe é o banco (migration 254, `novidade_da_hora`): a mais antiga com
 * nota que ESTA pessoa ainda não viu, e a mesma durante a hora inteira. Aqui só
 * perguntamos: ao abrir, na virada da hora, quando a aba volta ao foco e a cada
 * 5 min. Paciente nunca recebe nada (a função devolve vazio).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabaseClient';

export interface NovidadeDaHora {
  versao: string;
  numero: number | null;
  tipo: string | null;
  url: string | null;
  notaPt: string;
  notaEn: string;
  data: string | null;
  jaExibida: boolean;
}

const INTERVALO_MS = 5 * 60 * 1000;

/**
 * Quanto falta para virar a hora NA CLÍNICA (fuso de Nova York), mesmo que o
 * navegador esteja em outro fuso. É o que define quanto tempo a novidade fica.
 */
export function msAteVirarAHora(): number {
  try {
    const partes = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York', minute: '2-digit', second: '2-digit', hour12: false,
    }).formatToParts(new Date());
    const min = Number(partes.find(p => p.type === 'minute')?.value ?? 0);
    const seg = Number(partes.find(p => p.type === 'second')?.value ?? 0);
    return Math.max(5_000, ((59 - min) * 60 + (60 - seg)) * 1000);
  } catch {
    return 30 * 60 * 1000;   // fuso indisponível: meia hora é um meio razoável
  }
}

export function useNovidadeDaHora(ativo: boolean) {
  const [novidade, setNovidade] = useState<NovidadeDaHora | null>(null);
  const viradaId = useRef<number | null>(null);

  const buscar = useCallback(async () => {
    if (!ativo) return;
    try {
      const { data, error } = await supabase.rpc('novidade_da_hora');
      if (error) return;                       // silencioso: aviso nunca quebra a tela
      const l: any = Array.isArray(data) ? data[0] : data;
      setNovidade(l?.versao ? {
        versao: String(l.versao),
        numero: typeof l.numero === 'number' ? l.numero : null,
        tipo: l.tipo ?? null,
        url: typeof l.url === 'string' && l.url.startsWith('/') ? l.url : null,
        notaPt: String(l.nota_pt || '').split('\n')[0] || '',
        notaEn: String(l.nota_en || '').split('\n')[0] || '',
        data: l.data ?? null,
        jaExibida: l.ja_exibida === true,
      } : null);
    } catch { /* idem */ }
  }, [ativo]);

  useEffect(() => {
    if (!ativo) return;
    buscar();
    const periodico = window.setInterval(buscar, INTERVALO_MS);
    // e exatamente na virada da hora, que é quando a novidade pode trocar
    const agendarVirada = () => {
      if (viradaId.current) window.clearTimeout(viradaId.current);
      viradaId.current = window.setTimeout(() => { buscar(); agendarVirada(); }, msAteVirarAHora() + 2_000);
    };
    agendarVirada();
    const aoVoltar = () => { if (document.visibilityState === 'visible') buscar(); };
    document.addEventListener('visibilitychange', aoVoltar);
    window.addEventListener('focus', buscar);
    return () => {
      window.clearInterval(periodico);
      if (viradaId.current) window.clearTimeout(viradaId.current);
      document.removeEventListener('visibilitychange', aoVoltar);
      window.removeEventListener('focus', buscar);
    };
  }, [ativo, buscar]);

  return { novidade, recarregar: buscar };
}

export default useNovidadeDaHora;
